import { heroCards, heroStats } from "@/mocks/homeContent";

function EntranceCard({
  code,
  title,
  desc,
  count,
  tone,
}: {
  code: string;
  title: string;
  desc: string;
  count: string;
  tone: string;
}) {
  const gold = tone === 'gold';
  return (
    <a
      href="#shelf"
      className={`group flex flex-col rounded-xl border p-5 transition-transform duration-300 hover:-translate-y-1 ${
        gold
          ? 'border-[#d3a854]/50 bg-gradient-to-br from-[#d8ac55] to-[#bf8f34] text-[#241f18]'
          : 'border-[#3a3126] bg-[#251f18] text-[#efe5d2] hover:border-[#5b4c34]'
      }`}
    >
      <div className="flex items-center justify-between">
        <span
          className={`font-editorial text-lg italic ${gold ? 'text-[#241f18]/60' : 'text-[#c8a45f]'}`}
        >
          {code}
        </span>
        <i
          className={`ri-arrow-right-up-line text-lg ${gold ? 'text-[#241f18]/70' : 'text-[#c8a45f]'}`}
          aria-hidden="true"
        />
      </div>
      <h3 className="mt-4 text-xl font-bold tracking-wide">{title}</h3>
      <p
        className={`mt-2 flex-1 text-[13px] leading-relaxed ${gold ? 'text-[#241f18]/75' : 'text-[#a89a7f]'}`}
      >
        {desc}
      </p>
      <span
        className={`mt-4 inline-flex w-fit items-center gap-1.5 rounded-full px-3 py-1 text-[12px] font-semibold ${
          gold ? 'bg-[#241f18]/15 text-[#241f18]' : 'bg-[#e3b04f]/15 text-[#e3b04f]'
        }`}
      >
        <i className="ri-vip-crown-2-line text-[12px]" aria-hidden="true" />
        {count}
      </span>
    </a>
  );
}

export default function Hero() {
  return (
    <section id="top" className="w-full">
      <div className="mx-auto w-full max-w-[1240px] px-4 pb-0 md:px-6">
        {/* dark vault panel */}
        <div className="relative overflow-hidden rounded-2xl bg-[#1a1510] px-5 py-12 text-[#efe5d2] md:px-12 md:py-16">
          {/* subtle decorative glow */}
          <div
            aria-hidden="true"
            className="pointer-events-none absolute -right-24 -top-32 h-80 w-80 rounded-full opacity-20"
            style={{
              background:
                'radial-gradient(circle, rgba(216,172,85,0.9), transparent 70%)',
            }}
          />
          <div
            aria-hidden="true"
            className="pointer-events-none absolute -bottom-40 -left-24 h-80 w-80 rounded-full opacity-10"
            style={{
              background:
                'radial-gradient(circle, rgba(216,172,85,0.9), transparent 70%)',
            }}
          />

          <div className="relative w-full">
            <span className="inline-flex items-center gap-2 rounded-full border border-[#3f362a] bg-[#241e16] px-3 py-1 text-[11px] font-medium tracking-[0.2em] text-[#c8a45f]">
              <i className="ri-bookmark-3-fill text-[12px]" aria-hidden="true" />
              HEI LIAO SHE · 私藏档库
            </span>

            <div className="mt-6 flex w-full flex-col items-start justify-between gap-6 lg:flex-row lg:items-end">
              <div className="max-w-2xl">
                <h1 className="font-song text-[34px] font-semibold leading-[1.2] text-[#f6eeda] md:text-[46px]">
                  把黑料舍的
                  <span className="text-[#e3b04f]">私藏存货</span>
                  <br className="hidden md:block" />
                  码整齐，想挖哪条都有门路。
                </h1>
                <p className="mt-4 max-w-xl text-[15px] leading-relaxed text-[#b7a98d]">
                  整理好的宝库资源随时翻，干货家底一次看够；热门爆料不迷路，老料新料都给你
                  归到对应分区，入舍找料更省心。
                </p>
              </div>

              <div className="flex flex-col gap-3 sm:flex-row">
                <a
                  href="#collections"
                  className="whitespace-nowrap rounded-md bg-[#e3b04f] px-6 py-3 text-center text-sm font-bold text-[#241f18] transition-colors hover:bg-[#f0c56b]"
                >
                  翻一份干货合集
                </a>
                <a
                  href="#guide"
                  className="whitespace-nowrap rounded-md border border-[#4a4134] bg-transparent px-6 py-3 text-center text-sm font-semibold text-[#efe5d2] transition-colors hover:border-[#c8a45f] hover:text-[#e3b04f]"
                >
                  先看找料指引
                </a>
              </div>
            </div>

            {/* multi-card entrance — first screen dominant */}
            <div className="mt-10 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {heroCards.map((c) => (
                <EntranceCard key={c.code} {...c} />
              ))}
            </div>
          </div>
        </div>

        {/* stat band */}
        <div className="mt-4 grid grid-cols-2 gap-px overflow-hidden rounded-xl border border-[#ded2bb] bg-[#ded2bb] lg:grid-cols-4">
          {heroStats.map((s) => (
            <div key={s.label} className="flex flex-col items-center gap-1 bg-[#f8f3e8] px-4 py-6 text-center">
              <span className="font-editorial text-2xl font-semibold tracking-tight text-[#241f18] md:text-[28px]">
                {s.value}
              </span>
              <span className="text-[12px] tracking-wide text-[#8a7a5c]">{s.label}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}